
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Copy, Check, Info, Layers, Package, Settings } from 'lucide-react';
import { getUnifiedVersionInfo, getSupportVersionInfo, copyVersionInfoToClipboard } from '@/utils/unifiedVersioning';
import { toast } from 'sonner';

export const UnifiedAboutTab = () => {
  const [copied, setCopied] = useState(false);
  const versionInfo = getUnifiedVersionInfo();

  const handleCopyVersionInfo = async () => {
    const success = await copyVersionInfoToClipboard();
    if (success) {
      setCopied(true);
      toast.success('Version information copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy version information');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">About InvenioCRM</h2>
        <p className="text-muted-foreground mt-2">
          Version information and system details
        </p>
      </div>

      {/* Main Version Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Application Version
          </CardTitle>
          <CardDescription>
            Core application and system version information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">App Version:</span>
                <Badge variant="secondary" className="font-mono">
                  {versionInfo.app.fullVersion}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Landing Page:</span>
                <Badge variant="outline" className="font-mono">
                  v{versionInfo.landing.version}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Package:</span>
                <Badge variant="outline" className="font-mono">
                  {versionInfo.system.package}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Environment:</span>
                <Badge variant={versionInfo.system.environment === 'production' ? 'default' : 'secondary'}>
                  {versionInfo.system.environment}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Browser:</span>
                <span className="text-sm text-muted-foreground">{versionInfo.system.browser}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Build Date:</span>
                <span className="text-sm text-muted-foreground font-mono">
                  {new Date(versionInfo.system.build).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page Versions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Page Versions
          </CardTitle>
          <CardDescription>
            Individual page version information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(versionInfo.app.pageVersions).map(([page, version]) => (
              <div key={page} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                <span className="text-sm font-medium capitalize">
                  {page.replace(/([A-Z])/g, ' $1').trim()}:
                </span>
                <Badge variant="outline" className="text-xs">
                  {version}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Support Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Support Information
          </CardTitle>
          <CardDescription>
            Copy detailed version information for customer support
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Click the button below to copy comprehensive version information to your clipboard. 
              This information is useful when contacting support or reporting issues.
            </p>
            <Button 
              onClick={handleCopyVersionInfo}
              variant="outline"
              className="w-full md:w-auto"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Version Info for Support
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator />
      
      <div className="text-center text-sm text-muted-foreground">
        <p>InvenioCRM - AI-Powered Recruitment Platform</p>
        <p className="mt-1">© 2024 Invenio. All rights reserved.</p>
      </div>
    </div>
  );
};
